import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { V3_DENSITY_PRESETS, V3_PREVIEW_COPY } from '../../lib/v3ContentCapacity';

const ROLE_TO_SEMANTIC = {
  title: 'h1', subtitle: 'h2', eyebrow: 'h3', paragraph: 'paragraph', quote: 'paragraph',
  cta: 'cta', label: 'client_name', footer: 'brand_name', h1: 'h1', h2: 'h2', h3: 'h3',
};
const ALIGN_OPTIONS = [
  { value: 'TOP_ANCHORED', label: 'Top', position: 'top', justify: 'flex-start' },
  { value: 'CENTERED', label: 'Middle', position: 'middle', justify: 'center' },
  { value: 'BOTTOM_ANCHORED', label: 'Low', position: 'bottom', justify: 'flex-end' },
];

function semanticFor(template, block) {
  const role = block?.role || 'paragraph';
  return template?.builder_style?.semantic_map?.[role] || ROLE_TO_SEMANTIC[role] || role;
}

function effectiveVerticalAlignment(template, semantic, block, overrides = {}) {
  const builder = template?.builder_style || {};
  const sourceSlot = template?.source_slot || '';
  const global = builder?.global_roles?.[semantic] || {};
  const local = sourceSlot ? builder?.archetype_overrides?.[sourceSlot]?.roles?.[semantic] || {} : {};
  return overrides?.[semantic] || local.vertical_alignment || global.vertical_alignment || block?.vertical_alignment || 'TOP_ANCHORED';
}

function findTextPreviewNodes(root) {
  const canvas = root?.querySelector('[data-testid="v3-live-view-layout"] .touch-none.select-none');
  if (!canvas) return [];
  return Array.from(canvas.children)
    .filter(node => node?.classList?.contains('cursor-move'))
    .map(node => node.firstElementChild)
    .filter(child => child?.tagName === 'DIV' && child.classList.contains('pointer-events-none'));
}

function selectedSemanticFromPanel(panel) {
  const typeSelect = panel?.querySelector('label select');
  const role = typeSelect?.value || '';
  return ROLE_TO_SEMANTIC[role] || role;
}

function VerticalAlignIcon({ position }) {
  const anchor = position === 'top' ? 'top-[4px]' : position === 'bottom' ? 'bottom-[4px]' : 'top-1/2 -translate-y-1/2';
  return <span className="relative block h-8 w-9 rounded-md border border-current/45" aria-hidden="true">
    <span className={`absolute left-1/2 -translate-x-1/2 ${anchor} flex w-6 flex-col gap-[2px]`}>
      <span className="h-[2px] w-6 rounded-full bg-current"/>
      <span className="h-[2px] w-4 rounded-full bg-current"/>
      <span className="h-[2px] w-5 rounded-full bg-current"/>
    </span>
  </span>;
}

export default function DesignV3CapacityPreview({
  template,
  editorRootRef,
  preset,
  onPresetChange,
  capacity,
  onVerticalAlign,
  verticalAlignmentOverrides = {},
}) {
  const [mountNode, setMountNode] = useState(null);
  const [selectedSemantic, setSelectedSemantic] = useState('');

  useEffect(() => {
    const root = editorRootRef?.current;
    if (!root) return undefined;
    let active = true;

    const syncInlineControls = () => {
      if (!active) return;
      const panel = root.querySelector('[data-testid="v3-typography-panel"]');
      const horizontalAlign = panel?.querySelector('[data-testid="v3-paragraph-alignment"]');
      const horizontalSection = horizontalAlign?.parentElement;
      if (!panel || !horizontalSection) {
        setMountNode(null);
        setSelectedSemantic('');
        return;
      }

      let host = panel.querySelector('[data-v3-inline-capacity-controls="true"]');
      if (!host) {
        host = document.createElement('div');
        host.dataset.v3InlineCapacityControls = 'true';
        horizontalSection.insertAdjacentElement('afterend', host);
      }
      setMountNode(current => current === host ? current : host);
      setSelectedSemantic(selectedSemanticFromPanel(panel));
    };

    syncInlineControls();
    const timer = window.setInterval(syncInlineControls, 180);

    return () => {
      active = false;
      window.clearInterval(timer);
      root.querySelector('[data-v3-inline-capacity-controls="true"]')?.remove();
    };
  }, [editorRootRef]);

  useEffect(() => {
    const root = editorRootRef?.current;
    if (!root) return undefined;
    let frame = null;

    const applyPreview = () => {
      frame = null;
      const nodes = findTextPreviewNodes(root);
      const blocks = Array.isArray(template?.layout?.text_blocks) ? template.layout.text_blocks : [];
      const copy = V3_PREVIEW_COPY[preset] || V3_PREVIEW_COPY['50-80'];
      blocks.forEach((block, index) => {
        const node = nodes[index];
        if (!node) return;
        const semantic = semanticFor(template, block);
        const value = copy?.[semantic];
        if (value && node.textContent !== value) node.textContent = value;
        const vertical = effectiveVerticalAlignment(template, semantic, block, verticalAlignmentOverrides);
        const option = ALIGN_OPTIONS.find(item => item.value === vertical) || ALIGN_OPTIONS[0];
        node.style.display = 'flex';
        node.style.flexDirection = 'column';
        node.style.justifyContent = option.justify;
      });
    };

    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(applyPreview);
    };

    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [editorRootRef, preset, template, verticalAlignmentOverrides]);

  const blocks = Array.isArray(template?.layout?.text_blocks) ? template.layout.text_blocks : [];
  const roleBlocks = Object.fromEntries(blocks.map(block => [semanticFor(template, block), block]));
  const block = roleBlocks[selectedSemantic];
  const currentAlign = selectedSemantic
    ? effectiveVerticalAlignment(template, selectedSemantic, block, verticalAlignmentOverrides)
    : 'TOP_ANCHORED';
  const roleCapacity = selectedSemantic ? capacity?.roles?.[selectedSemantic] : null;
  const recommendedMax = Number(roleCapacity?.recommended_max_words || roleCapacity?.max_words || 0);

  const capacityPortal = mountNode && selectedSemantic ? createPortal(
    <div className="pt-2 border-t border-[var(--border-color)] space-y-3" data-testid="v3-capacity-preview">
      <div className="space-y-2" data-testid="v3-vertical-align-panel">
        <div className="text-[10px] text-[var(--text-muted)]">Posizione verticale</div>
        <div className="grid grid-cols-3 gap-2">
          {ALIGN_OPTIONS.map(option => {
            const active = currentAlign === option.value;
            return <button
              key={option.value}
              type="button"
              title={`Posizione verticale: ${option.label}`}
              aria-label={`Posizione verticale: ${option.label}`}
              aria-pressed={active}
              className={`btn-ghost min-h-14 flex-col gap-1 justify-center ${active ? 'text-fuchsia-200' : ''}`}
              style={active ? {
                borderColor: 'var(--accent-purple)',
                background: 'rgba(168,85,247,0.22)',
                color: '#f5d0fe',
                boxShadow: 'inset 0 0 0 1px rgba(217,70,239,0.2)',
              } : undefined}
              onClick={() => onVerticalAlign?.(selectedSemantic, option.value)}
              data-testid={`v3-vertical-${selectedSemantic}-${option.position}`}
              data-active={active ? 'true' : 'false'}
            >
              <VerticalAlignIcon position={option.position}/>
              <span className="text-[9px] font-semibold leading-none">{option.label}</span>
            </button>;
          })}
        </div>
      </div>

      <label className="block text-[10px]">Stress test contenuto
        <select className="input-dark text-xs w-full mt-1" value={preset} onChange={event => onPresetChange(event.target.value)} data-testid="v3-density-select">
          {V3_DENSITY_PRESETS.map(item => <option key={item.key} value={item.key}>{item.label}{item.key === 'brief' ? '' : ' parole'}</option>)}
        </select>
      </label>

      {recommendedMax > 0 && <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border-color)] bg-black/10 px-2.5 py-2" data-testid="v3-role-capacity-profile">
        <span className="text-[9px] uppercase tracking-[.1em] text-[var(--text-muted)]">Capacità consigliata</span>
        <span className="text-[10px] font-semibold text-fuchsia-200">{selectedSemantic.toUpperCase()} ≤ {recommendedMax} parole</span>
      </div>}
    </div>,
    mountNode,
  ) : null;

  return <>{capacityPortal}</>;
}